import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collateralApi, ApiError } from '../services/collateralApi';
import type { InventoryListItem } from '../types/Inventory';

export default function useInventoryPageList() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [showNewForm, setShowNewForm] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('updatedAt');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [inventoryType, setInventoryType] = useState('');
    const [archived, setArchived] = useState('false');
    const [rows, setRows] = useState<InventoryListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [filterCounts, setFilterCounts] = useState<Record<string, any>>({});
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });

    const [selectedInventory, setSelectedInventory] = useState<any>(null);
    const [inventoryDetail, setInventoryDetail] = useState<any>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    const requestSeq = useRef(0);
    const detailSeq = useRef(0);

    const fetchData = useCallback(async () => {
        const requestId = ++requestSeq.current;
        setIsLoading(true);
        try {
            const result = await collateralApi.getInventories({
                page,
                pageSize,
                search: search || undefined,
                inventoryType: inventoryType || undefined,
                archived: archived || undefined,
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
            });
            if (requestId !== requestSeq.current) return;
            setRows(result.items || []);
            setTotal(result.total || 0);
            setFilterCounts(result.filterCounts || {});
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
        } catch (e: any) {
            if (requestId !== requestSeq.current) return;
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            if (requestId === requestSeq.current) {
                setIsLoading(false);
                setInitialLoading(false);
            }
        }
    }, [page, pageSize, search, inventoryType, archived, sortField, sortOrder, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const refresh = () => setRefreshKey(k => k + 1);

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setPage(1);
    };

    const handleRowClick = async (row: InventoryListItem) => {
        const detailId = ++detailSeq.current;
        setSelectedInventory(row);
        setInventoryDetail(null);
        setIsLoadingDetail(true);
        try {
            const detail = await collateralApi.getInventoryView(row.id);
            if (detailId !== detailSeq.current) return;
            setInventoryDetail(detail);
        } catch {
            if (detailId !== detailSeq.current) return;
            setInventoryDetail(null);
        } finally {
            if (detailId === detailSeq.current) setIsLoadingDetail(false);
        }
    };

    const closePanel = () => {
        detailSeq.current++;
        setSelectedInventory(null);
        setInventoryDetail(null);
        setIsLoadingDetail(false);
    };

    return {
        rows, total, page, setPage, pageSize, setPageSize: handlePageSizeChange,
        isLoading, initialLoading, filterCounts,
        showNewForm, setShowNewForm,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        inventoryType, setInventoryType,
        archived, setArchived,
        refresh, handleRowClick,
        selectedInventory, inventoryDetail, isLoadingDetail, closePanel,
        apiInfo,
    };
}
